import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Modal, Table, Input, Card, message, Spin, notification, Space } from "antd";
import { SearchOutlined, CheckOutlined } from "@ant-design/icons";
import { scheduleService, flightLegService } from "../../../../api/enhancedApi";
import enhancedApi from "../../../../api/enhancedApi";
import { useScheduleEditor } from "../context/ScheduleEditorContext";
import dayjs from "dayjs";
import { createUtcTimeString } from "../../../../utils/timezoneUtils";
import { calculateNextFlightTime, formatDuration } from "../../../../utils/timeUtils";
import { useTimeZone } from "../../../../contexts/TimeZoneContext";
import { formatMinutesToHHMM } from "../../../../utils/dateUtils";
import { DEFAULT_STC_TYPE, getStcTypeLabel } from "../../../../constants/flightConstants";
import { InlineHelpButton } from "../../../../components/common/HelpButton";
import { getAirportMinGroundTime } from "../../../../utils/minGroundTimeUtils";
import ConstraintsService from "../services/ConstraintsService";
import { pickBestMatchRoute, routeAllowsAircraft } from "../../shared/routeBinding";

// 高亮匹配文本（支持 "CGO-KGF" 形式：按 "-" 拆分后任一部分匹配即高亮）
const Highlight = ({ text, search }) => {
    if (!search || !text) return <span>{text}</span>;

    const lowerText = text.toLowerCase();
    const searchParts = search
        .toLowerCase()
        .split("-")
        .map((s) => s.trim())
        .filter(Boolean);

    let index = -1;
    let matchedLength = 0;
    for (const part of searchParts) {
        const i = lowerText.indexOf(part);
        if (i !== -1) {
            index = i;
            matchedLength = part.length;
            break;
        }
    }

    if (index === -1) return <span>{text}</span>;

    const before = text.substring(0, index);
    const matched = text.substring(index, index + matchedLength);
    const after = text.substring(index + matchedLength);

    return (
        <span>
            {before}
            <span style={{ backgroundColor: "rgba(24, 144, 255, 0.15)", color: "#1890ff", padding: "0 2px", borderRadius: "2px" }}>{matched}</span>
            {after}
        </span>
    );
};

const LegSelector = ({ visible, onCancel, onSelect, prevFlight, schedule, insertIndex = null }) => {
    const { state, addFlight, setFlights, setAirportConstraints } = useScheduleEditor();
    const { timeZone } = useTimeZone();
    const [loading, setLoading] = useState(false);
    const [routesLoading, setRoutesLoading] = useState(false);
    const [legs, setLegs] = useState([]);
    const [routes, setRoutes] = useState([]);
    const [searchText, setSearchText] = useState("");
    const [selectedRow, setSelectedRow] = useState(null);
    const searchInputRef = useRef(null);
    const [data, setData] = useState([]);

    // 自动匹配航路的辅助函数 - 使用后端优化的选择逻辑
    const autoMatchFlightRoute = async (departureAirport, arrivalAirport, aircraftReg) => {
        try {
            const response = await enhancedApi.flightRoute.getBestMatchRoute(departureAirport, arrivalAirport, aircraftReg);
            const route = response?.data;

            if (route) {
                return {
                    flight_route_id: route.id,
                    min_block_time_minutes: route.block_time_minutes || null,
                    route_name: route.route_name,
                };
            }

            return null;
        } catch (error) {
            console.error("自动匹配航路失败:", error);
            return null;
        }
    };

    // 加载航段和航线数据
    useEffect(() => {
        if (visible) {
            fetchLegs();
            fetchRoutes();

            let defaultSearchValue = "";

            // 情况1: 有前序航班 (从表格内部添加)
            if (prevFlight && prevFlight.arrival_airport) {
                defaultSearchValue = prevFlight.arrival_airport;
            }
            // 情况2: 没有前序航班 (从表头或空状态添加)
            else {
                // 过滤掉特殊行，获取真实的航班列表
                const realFlights = (state.flights || []).filter((f) => !f.isAddRow && !f.isAddHeaderRow);

                // 如果航班列表不为空，取最后一个航班的到达机场
                if (realFlights.length > 0) {
                    const lastFlight = realFlights[realFlights.length - 1];
                    if (lastFlight && lastFlight.arrival_airport) {
                        defaultSearchValue = lastFlight.arrival_airport;
                    }
                }
                // 如果航班列表为空，尝试从运输需求获取第一个始发地
                else if (state.parameters?.transportation_demands && state.parameters.transportation_demands.length > 0 && state.parameters.transportation_demands[0]?.origin) {
                    defaultSearchValue = state.parameters.transportation_demands[0].origin;
                }
            }

            setSearchText(defaultSearchValue);

            // 弹窗可见时，延迟聚焦搜索框并选中文本
            setTimeout(() => {
                if (searchInputRef.current) {
                    searchInputRef.current.focus();
                    searchInputRef.current.select();
                }
            }, 300);
        }
    }, [visible, prevFlight, state.flights, state.parameters?.transportation_demands]); // 添加依赖 state.flights 和 state.parameters.transportation_demands

    // 合并航段和航线数据，并为航段加载航路信息
    useEffect(() => {
        const loadLegsWithRoutes = async () => {
            const aircraftReg = schedule?.aircraft?.registration;

            // 处理航段数据，使用后端已经附带的航路信息，无需额外API调用
            const legsWithRoutes = legs.map((leg) => {
                // 后端已经返回了available_routes和default_route
                const availableRoutes = leg.available_routes || [];
                // 与后端 best-match 同一套顺序：默认且这架飞机能用 → 适配这架飞机 → 这架飞机能用的
                const bestMatchRoute = pickBestMatchRoute(availableRoutes, aircraftReg);

                return {
                    ...leg,
                    record_type: "leg",
                    key: `leg-${leg.departure_airport}-${leg.arrival_airport}`,
                    available_routes: availableRoutes,
                    best_match_route: bestMatchRoute,
                };
            });

            const combinedData = [
                ...legsWithRoutes,
                ...routes.map((route, index) => ({
                    ...route,
                    record_type: "route",
                    key: `route-${index}-${route.path || ""}`,
                    // 处理路径为显示格式
                    display_path: route.path?.split("-").join(" → ") || "",
                })),
            ];
            setData(combinedData);
        };

        if (legs.length > 0) {
            loadLegsWithRoutes();
        } else {
            // 如果没有航段数据，只显示航线
            const combinedData = routes.map((route, index) => ({
                ...route,
                record_type: "route",
                key: `route-${index}-${route.path || ""}`,
                display_path: route.path?.split("-").join(" → ") || "",
            }));
            setData(combinedData);
        }
    }, [legs, routes, schedule?.aircraft?.registration]);

    // 获取航段数据
    const fetchLegs = async () => {
        try {
            setLoading(true);
            const response = await flightLegService.getFlightLegList({ page_size: 500 }); // 添加page_size:500确保获取足够多的航段数据
            // 从 response.data.items 获取列表数据
            const legList = response?.data?.items || [];

            if (legList.length > 0) {
                setLegs(legList);
            } else {
                setLegs([]); // 确保在没有数据时设置为空数组
            }
        } catch (error) {
            console.error("获取航段数据失败:", error);
            message.error("获取航段数据失败");
        } finally {
            setLoading(false);
        }
    };

    // 获取航线数据
    const fetchRoutes = async () => {
        try {
            setRoutesLoading(true);

            // 准备请求参数 - 将transportation_demands转换为后端期望的格式
            const transportationDemands = state.parameters.transportation_demands || [];

            // 转换为后端期望的二维数组格式 [["出发机场", "到达机场"], ...]
            const demands = transportationDemands.map((demand) => [demand.origin, demand.destination]);

            const data = {
                demands: demands,
                max_transfers: 2, // 最大中转次数
            };

            const response = await scheduleService.calculateRoutes(data);
            const responseData = response?.data; // 从 response.data 获取数据

            // 处理响应数据 - 转换为前端需要的格式
            if (responseData && typeof responseData.demand_routes === "object") {
                // 将demand_routes对象转换为路由数组
                const routes = [];

                Object.entries(responseData.demand_routes).forEach(([key, pathsList]) => {
                    const [origin, destination] = key.split("-");

                    // 对每条路径创建一个路由对象
                    pathsList.forEach((pathObject) => {
                        // 将类数组对象转换为真实数组
                        const pathArray = Object.values(pathObject);

                        if (!Array.isArray(pathArray) || pathArray.length === 0) {
                            console.error("转换后的路径不是有效数组:", pathArray);
                            return; // 跳过无效路径
                        }

                        routes.push({
                            record_type: "route",
                            path: pathArray.join("-"), // 使用转换后的数组调用 join
                            origin: origin,
                            destination: destination,
                            total_bh: 120, // 默认值
                            legs_bh: Array(pathArray.length - 1).fill(120), // 使用转换后的数组获取 length
                        });
                    });
                });

                // 按航段数量排序: 2段 -> 1段 -> 3段及以上
                const sortedRoutes = routes.sort((a, b) => {
                    // 计算a和b路径的航段数量
                    const segmentsA = a.path.split("-").length - 1;
                    const segmentsB = b.path.split("-").length - 1;

                    // 如果a是2个航段
                    if (segmentsA === 2) {
                        // 如果b也是2个航段，保持原顺序
                        if (segmentsB === 2) return 0;
                        // 其他情况a都排在前面
                        return -1;
                    }

                    // 如果b是2个航段，b排在前面
                    if (segmentsB === 2) return 1;

                    // 如果a是单航段
                    if (segmentsA === 1) {
                        // 如果b也是单航段，保持原顺序
                        if (segmentsB === 1) return 0;
                        // 其他情况(b是3个及以上航段)，a排在前面
                        return -1;
                    }

                    // 如果b是单航段，b排在前面
                    if (segmentsB === 1) return 1;

                    // 3个及以上航段情况，航段数少的优先
                    return segmentsA - segmentsB;
                });

                setRoutes(sortedRoutes);
            } else {
                console.warn("航线数据格式不正确:", responseData);
                message.warning("航线数据格式不正确");
                setRoutes([]);
            }
        } catch (error) {
            console.error("获取航线数据失败:", error);
            message.error("获取航线数据失败: " + (error.message || "未知错误"));
            setRoutes([]);
        } finally {
            setRoutesLoading(false);
        }
    };

    // 筛选合并数据，并按业务相关性排序：出发机场匹配优先于到达机场匹配
    const filteredData = useMemo(() => {
        if (!searchText) return data;

        const lowerSearchText = searchText.toLowerCase().trim();

        // 含 "-" 时按机场序列解析，例如 "CGO-KGF" 表示出发 CGO、到达 KGF，
        // "HKG-" 表示从 HKG 出发（到达不限）
        const isPairSearch = lowerSearchText.includes("-");
        const queryParts = isPairSearch ? lowerSearchText.split("-").map((s) => s.trim()) : [];
        const [depQuery = "", arrQuery = ""] = queryParts;

        const legMatchesPair = (item) => {
            const dep = item.departure_airport?.toLowerCase() || "";
            const arr = item.arrival_airport?.toLowerCase() || "";
            return (!depQuery || dep.startsWith(depQuery)) && (!arrQuery || arr.startsWith(arrQuery));
        };

        // 航线匹配：搜索的机场按顺序出现在路径中即可，允许中间隔站，
        // 例如 "CGO-BUD" 可以匹配 "CGO-KGF-BUD"
        const routeMatchesParts = (item) => {
            const pathAirports = item.path?.toLowerCase().split("-") || [];
            const wanted = queryParts.filter(Boolean);
            if (wanted.length === 0) return true;

            let pathIndex = 0;
            for (const part of wanted) {
                while (pathIndex < pathAirports.length && !pathAirports[pathIndex].startsWith(part)) {
                    pathIndex++;
                }
                if (pathIndex >= pathAirports.length) return false;
                pathIndex++;
            }
            return true;
        };

        const filtered = data.filter((item) => {
            if (item.record_type === "leg") {
                if (isPairSearch) {
                    return legMatchesPair(item);
                }
                return (item.departure_airport && item.departure_airport.toLowerCase().includes(lowerSearchText)) || (item.arrival_airport && item.arrival_airport.toLowerCase().includes(lowerSearchText)) || (item.leg_number && item.leg_number.toLowerCase().includes(lowerSearchText)) || (item.stc_type && item.stc_type.toLowerCase().includes(lowerSearchText));
            } else if (item.record_type === "route") {
                if (isPairSearch) {
                    return routeMatchesParts(item);
                }
                return item.path && item.path.toLowerCase().includes(lowerSearchText);
            }
            return false;
        });

        // 按业务相关性排序：用户添加航段时，搜索框中通常是前序航班的到达机场，
        // 因此"从该机场出发"的航段/航线比"到达该机场"的更符合业务意图
        const getRelevanceScore = (item) => {
            if (item.record_type === "leg") {
                if (isPairSearch) return 0; // 精确的"出发-到达"匹配最优先
                if (item.departure_airport && item.departure_airport.toLowerCase().includes(lowerSearchText)) return 0;
                return 1;
            }
            // route: 路径首站匹配优先
            const firstAirport = item.path?.split("-")[0]?.toLowerCase() || "";
            if (firstAirport.includes(isPairSearch ? depQuery : lowerSearchText)) return 2;
            return 3;
        };

        return filtered.sort((a, b) => getRelevanceScore(a) - getRelevanceScore(b));
    }, [data, searchText]);

    // 生成航班号
    const getFlightNumber = (leg = null) => {
        const prefix = state.parameters.flight_number_prefix || "XX";

        // 优先使用传入的leg参数，否则使用selectedRow
        const currentLeg = leg || selectedRow;

        // 获取当前选中的航段的leg_number
        const legNumber = currentLeg?.leg_number || "";

        // 获取当前计划使用的飞机的偏移量
        const offset = schedule?.aircraft?.flight_number_offset || 0;

        let number;

        // 如果有航段编号，用航段编号加上偏移量
        if (legNumber && !isNaN(parseInt(legNumber))) {
            const baseNumber = parseInt(legNumber);
            number = baseNumber + offset;
        } else {
            // 如果没有有效的航段编号，生成随机航班号
            number = Math.floor(Math.random() * 9000) + 1000; // 生成1000-9999之间的随机数
        }

        // 无论是从航段编号生成还是随机生成，都要检查敏感数字
        const sensitiveNumbers = [
            // 紧急电话号码
            110, 119, 120, 122, 911, 999,
            // 政治敏感数字
            64, 89, 6489, 8964,
            // 宗教敏感数字
            444, 4444,
            // 不吉利数字
            1314, 384, 250, 404, 666, 1366, 1399, 7456,
            // 其他可能敏感数字
            251, 253, 254, 667, 246, 918, 414,
        ];

        // 检查数字是否敏感，如果是则加10并再次检查，直到不是敏感数字为止
        function checkAndAdjustNumber(num) {
            while (sensitiveNumbers.includes(num) || num.toString().includes("64") || num.toString().includes("89") || num.toString().includes("44") || num.toString().includes("666")) {
                num += 2;
                if (num > 9999) {
                    num = 1000;
                }
            }
            return num;
        }

        number = checkAndAdjustNumber(number);

        return `${prefix}${number}`;
    };

    // 显示轻微的选中提示
    const showSelectionNotification = (recordInfo) => {
        notification.open({
            message: "已添加",
            description: `${recordInfo}`,
            icon: <CheckOutlined style={{ color: "#52c41a" }} />,
            placement: "top",
            duration: 3,
        });
    };

    // 加航段 / 加航线前按涉及机场读取机场主数据的当前值：编排页可能开了很久，
    // 期间主数据的最小过站已改，继续用打开页面时的旧值会排出过期时刻。
    // 拉取失败时退回页面已加载的约束并提示；两者都取不到时由后续按“缺配置”拦住。
    const fetchLatestConstraintsForAirports = async (airportCodes) => {
        const currentConstraints = state.constraints?.airportConstraints || {};
        const codes = Array.from(new Set((airportCodes || []).filter(Boolean)));
        if (codes.length === 0) {
            return currentConstraints;
        }

        try {
            const freshConstraints = await ConstraintsService.fetchAirportConstraints(codes);
            setAirportConstraints((prev) => ({ ...prev, ...freshConstraints }));
            return { ...currentConstraints, ...freshConstraints };
        } catch (error) {
            console.error("加航段前刷新机场约束失败:", error);
            message.warning("获取最新机场约束失败，将使用已加载的数据");
            return currentConstraints;
        }
    };

    // 【航段航路BH优化】处理航段选择 - 允许无航路选择，优先使用航路BH，否则使用航段BH
    const handleLegSelect = async (selectedLeg) => {
        try {
            setSelectedRow(selectedLeg);

            let routeMatch = null;
            let minBlockTimeMinutes = null;

            // 1. 尝试获取航路信息
            // 只认自动绑航路选出的那一条：一条这架飞机能用的都没有时不拿列表第一条凑数
            if (selectedLeg.best_match_route) {
                const bestRoute = selectedLeg.best_match_route;
                routeMatch = {
                    flight_route_id: bestRoute.id,
                    min_block_time_minutes: bestRoute.block_time_minutes,
                    route_name: bestRoute.route_name,
                };
                minBlockTimeMinutes = bestRoute.block_time_minutes;
            }

            // 【航路BH统一】不再使用航段BH作为fallback，强制要求航路数据
            // 如果没有航路，直接阻止选择
            if (!minBlockTimeMinutes || !routeMatch) {
                message.error(`该航段 ${selectedLeg.departure_airport}-${selectedLeg.arrival_airport} 没有可用航路，无法添加航班。请先前往航路管理页面添加对应航路。`);
                return; // 直接阻止，不创建航班
            }

            // 显示轻微提示 日期+航段
            let notificationText = `${selectedLeg.departure_airport} → ${selectedLeg.arrival_airport}`;
            notificationText += ` | 航路: ${routeMatch.route_name} (${formatDuration(routeMatch.min_block_time_minutes, "h_m")})`;

            showSelectionNotification(notificationText);

            // 计算相关时间和日期 - 使用timezoneUtils中的计算函数
            // 注意：如果minBlockTimeMinutes为空，calc函数可能需要处理
            // 先按到达站读取机场主数据当前值：加航段不必先点“刷新基础数据”
            const constraintsForLeg = await fetchLatestConstraintsForAirports([
                prevFlight?.arrival_airport,
                selectedLeg.arrival_airport,
            ]);
            const timeData = calculateNextFlightTime(selectedLeg, prevFlight, schedule, state.parameters.transportation_demands, timeZone, minBlockTimeMinutes, { airportConstraints: constraintsForLeg });

            // 确保必要字段存在
            if (!timeData.std || !timeData.sta) {
                throw new Error("无法计算航班起降时间");
            }

            // 最小过站只认到达机场主数据的明确配置（含 0 分钟）；机场没配就留空，
            // 不得先写 60（缺配置的拦截由上面的时刻计算负责）
            const minGroundTimeMinutes = getAirportMinGroundTime(constraintsForLeg, selectedLeg.arrival_airport);

            // 实际轮档时间默认使用最小轮档时间
            let blockTimeMinutes = minBlockTimeMinutes;

            // 创建临时航班对象 - 确保所有时间都是UTC格式
            let tempFlight = {
                id: `temp_${Date.now()}_${Math.floor(Math.random() * 10000)}`,
                flight_date: dayjs.utc(timeData.date).format("YYYY-MM-DD"), // 确保是UTC日期
                aircraft_reg: schedule.aircraft?.registration,
                flight_number: getFlightNumber(selectedLeg), // 传入selectedLeg作为参数
                departure_airport: selectedLeg.departure_airport,
                arrival_airport: selectedLeg.arrival_airport,
                std: timeData.std,
                sta: timeData.sta,
                stc_type: selectedLeg.stc_type || DEFAULT_STC_TYPE,
                min_block_time_minutes: minBlockTimeMinutes, // 最小轮档时间
                block_time_minutes: blockTimeMinutes, // 实际轮档时间
                min_ground_time_minutes: minGroundTimeMinutes, // 最小过站时间
                // ground_time_minutes不需要设置，它由系统根据前后航班计算
                remarks: "",
                schedule_id: schedule.id,
                leg_number: selectedLeg.leg_number || "", // 确保传递航段编号，即使为空
                // 计算星期几 - 基于UTC日期
                day_of_week: dayjs.utc(timeData.date).day() || 7, // 0表示周日，转为7
                // 添加航路信息
                flight_route_id: routeMatch?.flight_route_id || null,
                flight_route_name: routeMatch?.route_name || null,
                flight_route_block_time_minutes: routeMatch?.min_block_time_minutes || null,
            };

            // 确保STD和STA是ISO字符串格式
            if (tempFlight.std && !tempFlight.std.endsWith("Z")) {
                tempFlight.std = createUtcTimeString(tempFlight.flight_date, tempFlight.std, "Z");
            }

            if (tempFlight.sta && !tempFlight.sta.endsWith("Z")) {
                tempFlight.sta = createUtcTimeString(tempFlight.flight_date, tempFlight.sta, "Z");
            }

            // 调用选择回调
            onSelect([tempFlight]);
            onCancel(); // 关闭弹窗
        } catch (error) {
            console.error("航段选择错误:", error);
            message.error(`选择航段失败: ${error.message}`);
        }
    };

    // 【航段航路BH优化】获取行样式，为无可用航路的航段添加禁用样式
    // 【航段航路BH优化】获取行样式
    const getRowClassName = (record) => {
        // 允许选择无航路的航段，不再添加禁用样式
        // 如果是当前选中的行，添加样式
        return record === selectedRow ? "ant-table-row-selected" : "";
    };

    // 处理航线选择
    const handleRouteSelect = async (route) => {
        try {
            if (!route || !route.path) {
                message.warning("选择的航线无效");
                return;
            }

            setSelectedRow(route);
            showSelectionNotification(`航线: ${route.path.split("-").join(" → ")}`);

            // 解析航线路径
            const airports = route.path.split("-");
            if (airports.length < 2) {
                message.warning("航线至少需要包含两个机场");
                return;
            }

            // 创建多个航班
            const newFlights = [];
            let lastFlight = prevFlight;

            // 先按整条航线涉及的机场读取主数据当前值：加航线不必先点“刷新基础数据”
            const constraintsForRoute = await fetchLatestConstraintsForAirports(airports);

            // 遍历航线中的每对相邻机场，创建对应的航班
            for (let i = 0; i < airports.length - 1; i++) {
                const depAirport = airports[i];
                const arrAirport = airports[i + 1];

                // 在legs中查找对应的航段数据
                const matchedLeg = legs.find((leg) => leg.departure_airport === depAirport && leg.arrival_airport === arrAirport);

                // 创建航段对象，优先使用找到的航段数据，找不到时使用默认值
                const leg = {
                    departure_airport: depAirport,
                    arrival_airport: arrAirport,
                    stc_type: matchedLeg?.stc_type || DEFAULT_STC_TYPE,
                    block_time_minutes: matchedLeg?.block_time_minutes || route.legs_bh?.[i] || 120,
                    leg_number: matchedLeg?.leg_number || `${(i + 1) * 100}`, // 优先使用航段数据中的编号，没有则生成新编号
                };

                // 【航段航路BH优化】自动匹配航路 - 强制必须匹配到航路
                const aircraftReg = schedule.aircraft?.registration;
                const routeMatch = await autoMatchFlightRoute(depAirport, arrAirport, aircraftReg);

                // 【核心变更】唯一使用航路的BH，如果没有匹配到航路则跳过该航段
                if (!routeMatch || !routeMatch.min_block_time_minutes) {
                    message.warning(`航线 ${depAirport}-${arrAirport} 没有可用航路，已跳过该航段`);
                    continue; // 跳过该航段
                }

                const minBlockTimeMinutes = routeMatch.min_block_time_minutes;

                // 计算时间
                const timeData = calculateNextFlightTime(leg, lastFlight, schedule, state.parameters.transportation_demands, timeZone, minBlockTimeMinutes, { airportConstraints: constraintsForRoute });

                // 最小过站只认到达机场主数据的明确配置（含 0 分钟）；机场没配就留空，
                // 不得先写 60（缺配置的拦截由上面的时刻计算负责）
                const minGroundTimeMinutes = getAirportMinGroundTime(constraintsForRoute, leg.arrival_airport);

                // 实际轮档时间默认使用最小轮档时间
                let blockTimeMinutes = minBlockTimeMinutes;

                // 创建航班对象
                const flight = {
                    id: `temp_${Date.now()}_${i}_${Math.floor(Math.random() * 10000)}`,
                    flight_date: dayjs.utc(timeData.date).format("YYYY-MM-DD"),
                    aircraft_reg: schedule.aircraft?.registration,
                    flight_number: getFlightNumber(leg),
                    departure_airport: depAirport,
                    arrival_airport: arrAirport,
                    std: timeData.std,
                    sta: timeData.sta,
                    stc_type: leg.stc_type,
                    min_block_time_minutes: minBlockTimeMinutes, // 最小轮档时间
                    block_time_minutes: blockTimeMinutes, // 实际轮档时间
                    min_ground_time_minutes: minGroundTimeMinutes, // 最小过站时间
                    // ground_time_minutes不需要设置，它由系统根据前后航班计算
                    remarks: "",
                    schedule_id: schedule.id,
                    leg_number: leg.leg_number, // 添加航段编号
                    day_of_week: dayjs.utc(timeData.date).day() || 7,
                    // 添加航路信息
                    flight_route_id: routeMatch?.flight_route_id || null,
                    flight_route_name: routeMatch?.route_name || null,
                    flight_route_block_time_minutes: routeMatch?.min_block_time_minutes || null,
                };

                // 确保STD和STA是ISO字符串格式
                if (flight.std && !flight.std.endsWith("Z")) {
                    flight.std = createUtcTimeString(flight.flight_date, flight.std, "Z");
                }

                if (flight.sta && !flight.sta.endsWith("Z")) {
                    flight.sta = createUtcTimeString(flight.flight_date, flight.sta, "Z");
                }

                newFlights.push(flight);

                // 上一个航班成为下一个的前序航班
                lastFlight = flight;
            }

            // 调用选择回调
            onSelect(newFlights);
            onCancel(); // 关闭弹窗
        } catch (error) {
            console.error("航线选择错误:", error);
            message.error(`选择航线失败: ${error.message}`);
        }
    };

    // 处理行选择
    const handleRowSelect = (record) => {
        if (record.record_type === "leg") {
            handleLegSelect(record);
        } else if (record.record_type === "route") {
            handleRouteSelect(record);
        }
    };

    // 合并的表格列定义
    const columns = [
        {
            title: "航段/航线信息",
            key: "info",
            width: 200,
            render: (text, record) => {
                if (record.record_type === "leg") {
                    return (
                        <span>
                            <Highlight text={record.departure_airport} search={searchText} /> → <Highlight text={record.arrival_airport} search={searchText} />
                        </span>
                    );
                } else {
                    const formattedPath = record.path?.split("-").join(" → ") || "";
                    return <Highlight text={formattedPath} search={searchText} />;
                }
            },
        },
        {
            title: "飞行时间",
            key: "flight_time",
            width: 100,
            render: (text, record) => {
                // 只对航段记录显示飞行时间，航线记录不显示飞行时间
                if (record.record_type === "leg") {
                    // 【航路BH统一】只显示自动绑航路选出的那条的飞行时间：
                    // 没有可用航路时不拿列表第一条的时间冒充将被绑上的轮档
                    const minBlockTimeMinutes = record.best_match_route?.block_time_minutes || null;

                    // 如果没有航路时间，显示"-"表示无数据（而不是使用航段的时间）
                    return minBlockTimeMinutes ? formatMinutesToHHMM(minBlockTimeMinutes) : <span style={{ color: "#999" }}>-</span>;
                } else {
                    return null; // 航线不显示飞行时间
                }
            },
        },
        {
            title: "推荐航路",
            key: "route_info",
            width: 300,
            render: (text, record) => {
                if (record.record_type === "leg") {
                    if (record.best_match_route) {
                        const route = record.best_match_route;
                        const totalRoutes = record.available_routes?.length || 0;

                        // 判断推荐理由：自动绑优先带这架飞机能用的默认，默认与飞机适配同时成立时也先说明默认
                        let recommendReason = "";
                        if (route.is_default) {
                            recommendReason = "[默认]";
                        } else if (routeAllowsAircraft(route, schedule?.aircraft?.registration) && route.compatible_aircraft && route.compatible_aircraft.length > 0) {
                            recommendReason = "[飞机适配]";
                        }
                        if (!recommendReason && totalRoutes > 1) {
                            recommendReason = "[首选]";
                        }

                        return (
                            <div>
                                <div style={{ fontWeight: "bold", color: "#1890ff" }}>
                                    {route.route_name}
                                    {recommendReason && <span style={{ color: "#52c41a", fontSize: "11px" }}> {recommendReason}</span>}
                                </div>
                                <div style={{ fontSize: "12px", color: "#666" }}>BH: {route.block_time_minutes ? formatMinutesToHHMM(route.block_time_minutes) : "N/A"}</div>
                                {totalRoutes > 1 && <div style={{ fontSize: "12px", color: "#999" }}>共 {totalRoutes} 个可选航路</div>}
                            </div>
                        );
                    } else if (record.available_routes && record.available_routes.length === 0) {
                        return <span style={{ color: "#ff4d4f" }}>无适配航路</span>;
                    } else if (record.available_routes && record.available_routes.length > 0) {
                        // 有航路但一条这架飞机能用的都没有：不能加，也不再显示“加载中”
                        return <span style={{ color: "#ff4d4f" }}>无可用航路</span>;
                    } else {
                        return <span style={{ color: "#999" }}>加载中...</span>;
                    }
                } else {
                    return null; // 航线不显示航路信息
                }
            },
        },
        {
            title: "附加信息",
            key: "additional_info",
            width: 120,
            render: (text, record) => {
                if (record.record_type === "leg") {
                    const flightType = getStcTypeLabel(record.stc_type);
                    const legNumber = record.leg_number ? `编号: ${record.leg_number}` : "";

                    return (
                        <div>
                            {flightType && (
                                <div>
                                    <Highlight text={flightType} search={searchText} />
                                </div>
                            )}
                            {legNumber && (
                                <div>
                                    <Highlight text={legNumber} search={searchText} />
                                </div>
                            )}
                        </div>
                    );
                } else {
                    return <span>包含 {record.path?.split("-").length - 1 || 0} 个航段</span>;
                }
            },
        },
    ];

    return (
        <Modal
            title={
                <Space>
                    <span>选择航段/航线 ({timeZone === "UTC" ? "UTC" : "本地时区"})</span>
                    <InlineHelpButton contentPath="help/2025-08-22_flight-route-binding.md" title="航段选择与航路绑定功能说明" tooltip="查看航段选择和自动航路绑定的使用说明" />
                </Space>
            }
            open={visible}
            onCancel={onCancel}
            footer={null}
            width={1000}
            destroyOnHidden
        >
            <Input ref={searchInputRef} placeholder="搜索机场/航班性质/航段编号，支持 CGO-KGF 按出发-到达搜索" prefix={<SearchOutlined />} onChange={(e) => setSearchText(e.target.value)} value={searchText} style={{ marginBottom: 16 }} allowClear />

            {prevFlight && (
                <Card size="small" style={{ marginBottom: 16 }}>
                    <div>
                        <strong>前序航班:</strong> {prevFlight.flight_number}
                    </div>
                    <div>
                        <strong>航段:</strong> {prevFlight.departure_airport} → {prevFlight.arrival_airport}
                        <strong style={{ marginLeft: 16 }}>到达时间:</strong> {dayjs.utc(prevFlight.sta).format("YYYY-MM-DD HH:mm")}
                    </div>
                </Card>
            )}

            <Spin spinning={loading || routesLoading}>
                <Table
                    rowKey="key"
                    columns={columns}
                    dataSource={filteredData}
                    pagination={{ pageSize: 15 }}
                    size="small"
                    onRow={(record) => {
                        return {
                            onClick: () => handleRowSelect(record),
                            style: { cursor: "pointer" },
                        };
                    }}
                    rowClassName={getRowClassName}
                    loading={loading || routesLoading}
                />
            </Spin>
        </Modal>
    );
};

export default LegSelector;
